import { useState } from "react";
import { Platform } from "react-native";

import { CoverageEventsFlowStepWrapper } from "@/blocks/CoverageEventsFlowStepWrapper";
import {
  VnAccordion,
  VnAccordionContent,
  VnAccordionItem,
  VnAccordionTrigger,
} from "@/components/VnAccordion";
import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { BaseCheckboxInput } from "@/components/VnCheckboxInput/BaseCheckboxInput";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnSeparator } from "@/components/VnSeparator";
import { VnText } from "@/components/VnText/VnText";
import { VnTouchableOpacity } from "@/components/VnTouchableOpacity";
import {
  getExemptionI18nKey,
  toScreeningMembers,
  WORK_REQUIREMENT_EXEMPTION_GROUPS,
  WorkRequirementExemptionType,
  WorkRequirementType,
} from "@/domain/workRequirements";
import { useWorkRequirementScreeningStep } from "@/hooks/useWorkRequirementScreeningStep";
import { formatHouseholdMemberName } from "@/utils/householdMember";
import { cn } from "@/utils/tailwind";

// Exemption → the members it applies to. An exemption can be checked before
// anyone is picked for it, so a key with an empty list is still "checked".
type ExemptionSelectionType = Partial<
  Record<WorkRequirementExemptionType, string[]>
>;

type SelectionStateType = {
  exemptions: ExemptionSelectionType;
  isNoneSelected: boolean;
};

const getSavedSelection = (
  workRequirement: WorkRequirementType,
): SelectionStateType => {
  const exemptions: ExemptionSelectionType = {};
  for (const member of workRequirement.members) {
    for (const exemption of member.exemptions) {
      exemptions[exemption] = [
        ...(exemptions[exemption] ?? []),
        member.householdMemberId,
      ];
    }
  }

  const hasAnsweredExemptions =
    workRequirement.screeningLastStep !== null &&
    workRequirement.screeningLastStep !== "WHO";

  return {
    exemptions,
    isNoneSelected:
      hasAnsweredExemptions && Object.keys(exemptions).length === 0,
  };
};

export const WorkRequirementExemptionsScreen = () => {
  const { workRequirement, isLoading, isSaving, saveAndContinue, goBack } =
    useWorkRequirementScreeningStep();

  // Null until the patient edits, so saved answers show first and a
  // background refetch never overwrites their edits.
  const [editedSelection, setEditedSelection] =
    useState<SelectionStateType | null>(null);

  if (isLoading || !workRequirement) {
    return (
      <CoverageEventsFlowStepWrapper
        onBack={goBack}
        shouldShowNextButton={false}>
        <VnLoadingSpinner />
      </CoverageEventsFlowStepWrapper>
    );
  }

  const selection = editedSelection ?? getSavedSelection(workRequirement);
  const checkedExemptions = Object.keys(
    selection.exemptions,
  ) as WorkRequirementExemptionType[];

  const toggleExemption = (exemption: WorkRequirementExemptionType) => {
    const { [exemption]: current, ...others } = selection.exemptions;
    setEditedSelection({
      isNoneSelected: false,
      exemptions: current
        ? others
        : { ...selection.exemptions, [exemption]: [] },
    });
  };

  // An exemption's card is expanded exactly while it is checked.
  const handleAccordionChange = (openValues: string[]) => {
    const toggled =
      openValues.find(
        value =>
          !checkedExemptions.includes(value as WorkRequirementExemptionType),
      ) ?? checkedExemptions.find(exemption => !openValues.includes(exemption));

    if (toggled) {
      toggleExemption(toggled as WorkRequirementExemptionType);
    }
  };

  const toggleExemptionMember = ({
    exemption,
    householdMemberId,
  }: {
    exemption: WorkRequirementExemptionType;
    householdMemberId: string;
  }) => {
    const memberIds = selection.exemptions[exemption] ?? [];
    setEditedSelection({
      isNoneSelected: false,
      exemptions: {
        ...selection.exemptions,
        [exemption]: memberIds.includes(householdMemberId)
          ? memberIds.filter(id => id !== householdMemberId)
          : [...memberIds, householdMemberId],
      },
    });
  };

  const toggleNone = () =>
    setEditedSelection({
      isNoneSelected: !selection.isNoneSelected,
      exemptions: {},
    });

  const canContinue =
    selection.isNoneSelected ||
    (checkedExemptions.length > 0 &&
      checkedExemptions.every(
        exemption => (selection.exemptions[exemption] ?? []).length > 0,
      ));

  const handleNext = () =>
    saveAndContinue({
      lastStep: "EXEMPTIONS",
      members: toScreeningMembers(workRequirement).map(member => ({
        ...member,
        exemptions: checkedExemptions.filter(exemption =>
          selection.exemptions[exemption]?.includes(member.householdMemberId),
        ),
      })),
    });

  return (
    <CoverageEventsFlowStepWrapper
      onBack={goBack}
      onNext={handleNext}
      isNextButtonLoading={isSaving}
      isNextButtonDisabled={!canContinue}>
      <VnText
        variant="headerTwo"
        i18nKey="native:workRequirements.exemptions.title"
      />
      <VnText
        variant="lg"
        className="text-muted-foreground"
        i18nKey="native:workRequirements.exemptions.description"
      />

      <VnAccordion
        type="multiple"
        value={checkedExemptions}
        onValueChange={value => handleAccordionChange(value)}
        className="mt-4 flex w-full">
        <VnBox className="gap-y-5">
          {WORK_REQUIREMENT_EXEMPTION_GROUPS.map(group => (
            <VnBox key={group.i18nKey} className="gap-y-3">
              <VnText
                variant="mdMedium"
                className="text-muted-foreground"
                i18nKey={group.i18nKey}
              />
              {group.exemptions.map(exemption => (
                <VnCard key={exemption} className="mb-2 gap-4 rounded-md p-4">
                  <VnAccordionItem value={exemption}>
                    <VnAccordionTrigger
                      showChevronIcon={false}
                      className="w-full gap-1.5 py-0">
                      <VnBox className="flex min-w-0 flex-1 flex-row gap-x-2.5">
                        <BaseCheckboxInput
                          checked={Boolean(selection.exemptions[exemption])}
                          onCheckedChange={() => toggleExemption(exemption)}
                        />
                        <VnText
                          variant="lg"
                          i18nKey={getExemptionI18nKey(exemption)}
                          className="shrink"
                        />
                      </VnBox>
                    </VnAccordionTrigger>
                    <VnAccordionContent className="pb-0">
                      <VnBox className="bg-background mt-4 flex flex-col gap-2 rounded-sm p-4">
                        <VnText
                          i18nKey="common:whoApplies"
                          variant="mdMedium"
                        />
                        <VnBox className="mt-2 flex flex-col gap-2">
                          {workRequirement.members.map(member => {
                            const isSelected = Boolean(
                              selection.exemptions[exemption]?.includes(
                                member.householdMemberId,
                              ),
                            );
                            const toggleMember = () =>
                              toggleExemptionMember({
                                exemption,
                                householdMemberId: member.householdMemberId,
                              });

                            return (
                              <VnTouchableOpacity
                                key={member.householdMemberId}
                                onPress={toggleMember}
                                shouldDebounce={false}
                                className={cn(
                                  "flex min-h-8 flex-row items-start gap-x-2.5 rounded-md",
                                  Platform.select({ web: "cursor-pointer" }),
                                )}>
                                <VnBox className="pt-0.5">
                                  <BaseCheckboxInput
                                    checked={isSelected}
                                    onCheckedChange={toggleMember}
                                    className="bg-card web:pointer-events-none"
                                  />
                                </VnBox>
                                <VnText variant="md">
                                  {formatHouseholdMemberName(member)}
                                </VnText>
                              </VnTouchableOpacity>
                            );
                          })}
                        </VnBox>
                      </VnBox>
                    </VnAccordionContent>
                  </VnAccordionItem>
                </VnCard>
              ))}
              <VnSeparator />
            </VnBox>
          ))}

          <VnCard className="mb-5 gap-4 rounded-md border-t p-4">
            <VnTouchableOpacity
              onPress={toggleNone}
              shouldDebounce={false}
              className={cn(
                "flex min-w-0 flex-1 flex-row gap-x-2.5",
                Platform.select({ web: "cursor-pointer" }),
              )}>
              <BaseCheckboxInput
                checked={selection.isNoneSelected}
                onCheckedChange={toggleNone}
                className="web:pointer-events-none"
              />
              <VnText
                variant="md"
                i18nKey="common:noneOfTheAbove"
                className="w-full"
              />
            </VnTouchableOpacity>
          </VnCard>
        </VnBox>
      </VnAccordion>
    </CoverageEventsFlowStepWrapper>
  );
};
